import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import * as responsiveUtils from '~/lib/responsiveUtils';
import { useOnResponsivityChange, useBringOnTop } from '~/hooks';
import { getElementTransformPosition } from '~/lib/helpers';
import { DragCursor } from 'Cursors';

function ResponsiveDraggable(props) {
    const {
        children,
        size: refSize,
        defaultPosition,
        updatedPosition,
        isOnTop,
        onDragStop,
    } = props;

    // Store the last valid position in a ref to persist across re-renders
    const lastValidPositionRef = useRef(null);
    const [position, setPosition] = useState(null);
    const [animateToPosition, setAnimateToPosition] = useState(null);
    // dragConstraints must be passed to motion.div via state so that updating
    // the bounds (e.g. when the modal grows as financial ratios open) triggers
    // a re-render and framer-motion clamps live drags to the new bounds.
    // The ref mirror keeps a synchronous copy for the clamping logic in callbacks.
    const [draggingBounds, setDraggingBounds] = useState({});
    const positionRef = useRef({});
    const sizeRef = useRef({});
    const draggingBoundsRef = useRef({});
    const screenScaleRef = useRef(1);
    const screenHasPeakrowRef = useRef(false);
    const draggableRef = useRef();
    const isInitializedRef = useRef(false);
    const isDraggingRef = useRef(false);

    const updatePosition = useCallback((newPosition, shouldAnimate = true) => {
        if (!newPosition) return;

        const boundedPosition = getPositionWithinBounds(newPosition, draggingBoundsRef.current);
        lastValidPositionRef.current = boundedPosition; // Store the position

        if (shouldAnimate && !isDraggingRef.current) {
            setAnimateToPosition(boundedPosition);
        } else {
            setPosition(boundedPosition);
        }

        positionRef.current = boundedPosition;
    }, []);

    const applyDraggingBounds = useCallback((bounds) => {
        draggingBoundsRef.current = bounds;
        setDraggingBounds(bounds);
    }, []);

    const initialize = useCallback(() => {
        if (isInitializedRef.current) return;

        screenScaleRef.current = responsiveUtils.getScreenScale();
        screenHasPeakrowRef.current = responsiveUtils.hasPeakrow();
        isInitializedRef.current = true;
    }, []);

    useEffect(initialize, []);

    useEffect(() => {
        if (!refSize) return;

        const size = responsiveUtils.calculateSize(refSize);
        sizeRef.current = size;

        if (draggableRef.current) {
            setElementSize(draggableRef.current, size);
        }

        applyDraggingBounds(responsiveUtils.getDraggingBounds(size));

        // Only use defaultPosition if we don't have a stored position
        if (!lastValidPositionRef.current && defaultPosition) {
            const initialPosition = responsiveUtils.calculatePosition(defaultPosition);
            updatePosition(initialPosition, false);
        } else if (lastValidPositionRef.current) {
            // The size changed (e.g. financial ratios opened, widening the modal).
            // Re-clamp the current position to the new bounds and animate it back
            // fully on-screen.
            updatePosition(lastValidPositionRef.current, false);
        }
    }, [refSize, defaultPosition, updatePosition, applyDraggingBounds]);

    useEffect(() => {
        if (!updatedPosition) return;
        updatePosition(updatedPosition);
        lastValidPositionRef.current = updatedPosition; // Store updated position
    }, [updatedPosition, updatePosition]);

    const bringToTop = useCallback(() => {
        if (!draggableRef.current) return;

        if (!document.traders) {
            document.traders = { zIndex: 1 };
        }

        const nextZIndex = ++document.traders.zIndex;
        draggableRef.current.style.zIndex = nextZIndex;
    }, []);

    const handleDragStart = useCallback((event, info) => {
        isDraggingRef.current = true;
        bringToTop();
    }, [bringToTop]);

    const handleDragEnd = useCallback((event, info) => {
        if (!draggableRef?.current) return;

        isDraggingRef.current = false;
        const currentPosition = getElementTransformPosition(draggableRef.current);
        if (!currentPosition) return;

        positionRef.current = currentPosition;
        lastValidPositionRef.current = currentPosition; // Store the position after drag
        setPosition(currentPosition);
        setAnimateToPosition(null);
        onDragStop?.(event, currentPosition);
    }, [onDragStop]);

    useBringOnTop(draggableRef?.current, isOnTop);

    const onScreenResponsivityChange = useCallback(() => {
        if (!draggableRef?.current || !isInitializedRef.current) return;

        const newScreenScale = responsiveUtils.getScreenScale();
        const newScreenHasPeakrow = responsiveUtils.hasPeakrow();

        const currentPosition = lastValidPositionRef.current || getElementTransformPosition(draggableRef.current);
        if (!currentPosition) return;

        const scaledPosition = reCalculatePosition(
            currentPosition,
            screenScaleRef.current,
            newScreenScale
        );

        const yPos = updateYPosByPeakrow(
            scaledPosition.y,
            screenHasPeakrowRef.current,
            newScreenHasPeakrow
        );

        const newSize = responsiveUtils.calculateSize(refSize);
        sizeRef.current = newSize;
        applyDraggingBounds(responsiveUtils.getDraggingBounds(newSize));

        if (draggableRef.current) {
            setElementSize(draggableRef.current, newSize);
        }

        const newPosition = {
            x: scaledPosition.x,
            y: yPos
        };

        updatePosition(newPosition, true);

        screenScaleRef.current = newScreenScale;
        screenHasPeakrowRef.current = newScreenHasPeakrow;
    }, [refSize, updatePosition, applyDraggingBounds]);

    useOnResponsivityChange(onScreenResponsivityChange);

    const handleOnClick = useCallback((event) => {
        event.stopPropagation();
    }, []);

    const getAnimationProps = useCallback(() => {
        if (animateToPosition) {
            return {
                animate: {
                    opacity: 1,
                    x: animateToPosition.x,
                    y: animateToPosition.y,
                    transition: { duration: 0 }
                }
            };
        }

        if (position) {
            return {
                initial: false,
                animate: {
                    opacity: 1,
                    x: position.x,
                    y: position.y,
                    transition: { duration: 0.4 }
                }
            };
        }

        // Only use default position if we don't have a stored position
        if (!lastValidPositionRef.current && defaultPosition) {
            const { x, y } = responsiveUtils.calculatePosition(defaultPosition);
            return {
                initial: { opacity: 1, x: 0, y: 0 },
                animate: {
                    opacity: 1,
                    x,
                    y,
                    transition: { duration: 0 }
                }
            };
        }

        // Fallback: ref has a valid position but state hasn't committed yet.
        // Use the ref directly to avoid the opacity:0 initial state sticking.
        if (lastValidPositionRef.current) {
            return {
                animate: {
                    opacity: 1,
                    x: lastValidPositionRef.current.x,
                    y: lastValidPositionRef.current.y,
                    transition: { duration: 0 }
                }
            };
        }

        return {
            initial: INITIAL_MOTION
        };
    }, [position, animateToPosition, defaultPosition]);

    const animationProps = getAnimationProps();

    return (
        <motion.div
            drag
            dragConstraints={draggingBounds}
            dragElastic={0}
            onClick={handleOnClick}
            dragMomentum={false}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            css={DragCursor}
            style={DIV_STYLE}
            ref={draggableRef}
            id={props.id}
            {...animationProps}
        >
            {children}
        </motion.div>
    );
}

// Constants and helper functions remain the same...
const INITIAL_MOTION = { opacity: 0 };
const DIV_STYLE = { position: 'absolute' };

function getPositionWithinBounds(position, bounds) {
    if (!bounds || !position) return position;

    let { x, y } = position;

    x = Math.max(bounds.left, Math.min(x, bounds.right));
    y = Math.max(bounds.top, Math.min(y, bounds.bottom));

    return { x, y };
}

function reCalculatePosition(position, oldScreenScale, newScreenScale) {
    if (!position || !oldScreenScale || !newScreenScale) return position;

    const scaleDiff = newScreenScale / oldScreenScale;

    return {
        x: Math.round(position.x * scaleDiff),
        y: Math.round(position.y * scaleDiff)
    };
}

function updateYPosByPeakrow(newYPos, didOldScreenHasAPeakrow, doesNewScreenHavePeakrow) {
    const minYPos = -1 * responsiveUtils.getPeakrowHeight();

    if (didOldScreenHasAPeakrow && newYPos <= 0) {
        return doesNewScreenHavePeakrow
            ? Math.max(minYPos, newYPos)
            : 0;
    }

    if (newYPos === 0 && !didOldScreenHasAPeakrow && doesNewScreenHavePeakrow) {
        return minYPos;
    }

    return newYPos;
}

function setElementSize(element, size) {
    if (!element || !size) return;

    element.style.width = px(size.width);
    element.style.height = px(size.height);
}

function px(val) {
    return val && val !== 0 ? `${val}px` : '0px';
}

export default ResponsiveDraggable;
